import { DictionaryValuesService } from '../../shared/services/dictionaryValues.service';
import { Component, OnInit, ViewChild } from '@angular/core';
import { trigger, state, style } from '@angular/animations';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';

import { DatatableBaseComponent } from '@components/datatable-base.component';
import { UiService } from '@services/ui.service';
import { DictionaryValue } from '@classes/dictionary/dictionaryValue';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';

@Component({
  selector: 'app-transport-types',
  templateUrl: './transport-types.component.html',
  styleUrls: ['./transport-types.component.scss'],
  animations: [
    trigger('detailExpand', [
      state(
        'collapsed',
        style({ height: '0px', minHeight: '0', display: 'none' }),
      ),
      state('expanded', style({ height: '*' })),
    ]),
  ],
})
export class TransportTypesComponent
  extends DatatableBaseComponent
  implements OnInit
{
  transportTypes: DictionaryValue[];
  urgencyLevels: DictionaryValue[];
  displayedColumnsTransport = ['name', 'dictionary', 'client', 'site', 'temperature', 'actions'];
  displayedColumnsUrgency = ['name', 'dictionary', 'client', 'site', 'actions'];
  dataSourceTransportType: MatTableDataSource<DictionaryValue>;
  dataSourceUrgencyLevel: MatTableDataSource<DictionaryValue>;
  @ViewChild('TransportTypesSort', { static: true })
  public transportTypesSort: MatSort;
  @ViewChild('UrgencyLevelsSort', { static: true })
  public urgencyLevelsSort: MatSort;
  @ViewChild('TransportTypesPaginator', { static: true })
  transportTypesPaginator: MatPaginator;
  @ViewChild('UrgencyLevelsPaginator', { static: true })
  urgencyLevelsPaginator: MatPaginator;

  constructor(
    protected uiSrv: UiService,
    private route: ActivatedRoute,
    private dictionaryValuesSrv: DictionaryValuesService,
  ) {
    super(uiSrv);
  }

  ngOnInit() {
    super.ngOnInit();
    this.getRouteData();
    this.initSubscriptions();
  }

  private initTable(): void {
    this.dataSourceTransportType = new MatTableDataSource(this.transportTypes);
    this.dataSourceTransportType.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'dictionary':
          return item.dictionary.description;
        case 'client':
          return item.client.name;
        case 'temperature':
          return item.temperature !== null && item.temperature !== undefined ? item.temperature : '';
        case 'site':
          return item.sites?.map((x) => x.name).join(' ');
        default:
          return item[property];
      }
    };
    this.dataSourceTransportType.filterPredicate = (data, filter: string) => {
      const accumulator = (currentTerm, key) => {
        switch (key) {
          case 'client':
            return currentTerm + data.client.name;
          case 'sites':
            return currentTerm + data.sites.map((x) => x.name).join(' ');
          default:
            return currentTerm + data[key];
        }
      };
      const dataStr = Object.keys(data).reduce(accumulator, '').toLowerCase();
      // Transform the filter by converting it to lowercase and removing whitespace.
      const transformedFilter = filter.trim().toLowerCase();
      return dataStr.indexOf(transformedFilter) !== -1;
    };
    this.dataSourceTransportType.sort = this.transportTypesSort;
    this.dataSourceTransportType.paginator = this.transportTypesPaginator;

    this.dataSourceUrgencyLevel = new MatTableDataSource(this.urgencyLevels);
    this.dataSourceUrgencyLevel.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'dictionary':
          return item.dictionary.description;
        case 'client':
          return item.client.name;
        case 'site':
          return item.sites?.map((x) => x.name).join(' ');
        default:
          return item[property];
      }
    };
    this.dataSourceUrgencyLevel.filterPredicate = (data, filter: string) => {
      const accumulator = (currentTerm, key) => {
        switch (key) {
          case 'client':
            return currentTerm + data.client.name;
          case 'sites':
            return currentTerm + data.sites?.map((x) => x.name).join(' ');
          default:
            return currentTerm + data[key];
        }
      };
      const dataStr = Object.keys(data).reduce(accumulator, '').toLowerCase();
      // Transform the filter by converting it to lowercase and removing whitespace.
      const transformedFilter = filter.trim().toLowerCase();
      return dataStr.indexOf(transformedFilter) !== -1;
    };
    this.dataSourceUrgencyLevel.sort = this.urgencyLevelsSort;
    this.dataSourceUrgencyLevel.paginator = this.urgencyLevelsPaginator;
  }

  private getRouteData(): void {
    this.route.data.subscribe(
      (data: {
        transportTypes: DictionaryValue[];
        urgencyLevels: DictionaryValue[];
      }) => {
        this.transportTypes = data.transportTypes;
        this.urgencyLevels = data.urgencyLevels;
        this.initTable();
      },
    );
  }

  private initSubscriptions(): void {
    this.addSubscription(
      this.dictionaryValuesSrv.transportTypesListChanged.subscribe(
        (dictionaryValues) => {
          this.dataSourceTransportType.data = dictionaryValues || [];
          this.dataSourceTransportType.sort = this.transportTypesSort;
          this.onEditFinished();
        },
      ),
    );

    this.addSubscription(
      this.dictionaryValuesSrv.urgencyLevelsListChanged.subscribe(
        (dictionaryValues) => {
          this.dataSourceUrgencyLevel.data = dictionaryValues || [];
          this.dataSourceUrgencyLevel.sort = this.urgencyLevelsSort;
          this.onEditFinished();
        },
      ),
    );
  }

  deleteTypeTransport(idDictionaryValue: string) {
    const confirmation = confirm(
      'Êtes-vous certain de vouloir supprimer ce type de produit ?',
    );

    if (confirmation) {
      this.dictionaryValuesSrv
        .deleteTypeTransport(idDictionaryValue)
        .subscribe(() => {
          this.uiSrv.showSnackbar('repository.transportTypes.deleted', true);
        });
    }
  }

  deleteUrgencyLevel(idDictionaryValue: string) {
    const confirmation = confirm(
      "Êtes-vous certain de vouloir supprimer ce niveau d'urgence ?",
    );

    if (confirmation) {
      this.dictionaryValuesSrv
        .deleteUrgencyLevel(idDictionaryValue)
        .subscribe(() => {
          this.uiSrv.showSnackbar('repository.urgencyLevels.deleted', true);
        });
    }
  }

  applyFilterTransportType(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSourceTransportType.filter = filterValue.trim().toLowerCase();
  }

  applyFilterUrgencyLevel(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSourceUrgencyLevel.filter = filterValue.trim().toLowerCase();
  }
}
